import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { useJoinLesson, useTodayLessonLink, useToaster } from 'core';
import { ToastTypes } from 'components/atoms';
import { UserRole } from 'context';
import { isNow } from 'services/DateService';
import { LessonType } from 'types';

import { LessonZoomJoin } from './LessonZoomJoin';

/** How long before the start time the room may be entered. */
const JOIN_OPENS_MINUTES_BEFORE = 90;
const DEFAULT_START = '19:00:00';
const DEFAULT_END = '22:00:00';
/**
 * Время занятия в БД хранится в UTC (нулевой меридиан), а школа работает по
 * варшавскому времени — поэтому сырое `19:00:00` это 21:00 в Варшаве летом.
 *
 * Зона указана именованная, а не фиксированный «+2»: летом Warsaw = UTC+2,
 * зимой UTC+1, и жёсткая цифра увела бы окно входа на час при переходе на
 * зимнее время. Считать в локальной зоне браузера тоже нельзя — у студента с
 * другим часовым поясом (или просто иначе выставленной ОС) окно уехало бы на
 * разницу поясов.
 */
const SCHOOL_ZONE = 'Europe/Warsaw';

/** Сырое время занятия (UTC) → момент времени в варшавских стенных часах. */
const lessonMoment = (date: string, rawTime: string): DateTime =>
  DateTime.fromISO(`${date}T${rawTime}`, { zone: 'utc' }).setZone(SCHOOL_ZONE);

interface IJoinWindow {
  open: boolean;
  ended: boolean;
  startsAt: DateTime | null;
}

/**
 * The lesson's own time window. `hasJoinLink` alone is group-wide, so without
 * this the button rendered fully enabled on every lesson — a student browsing
 * lesson 3 a week early could click it, record a join click and land in a dead
 * room. Times come from the lesson (`startTime`/`endTime` are already in the
 * LMS query); a missing/invalid date leaves the window OPEN rather than
 * blocking a join we can't reason about.
 */
const buildJoinWindow = (
  now: DateTime,
  date?: string | null,
  startTime?: string | null,
  endTime?: string | null,
): IJoinWindow => {
  if (!date) {
    return { open: true, ended: false, startsAt: null };
  }
  const start = lessonMoment(date, startTime || DEFAULT_START);
  let end = lessonMoment(date, endTime || DEFAULT_END);
  if (!start.isValid || !end.isValid) {
    return { open: true, ended: false, startsAt: null };
  }
  // Занятие через полночь (например 22:00-01:00): дата в БД одна на занятие,
  // поэтому конец «раньше» начала. Без этого окно закрыто всегда, и кнопка
  // мертва на таких занятиях.
  if (end <= start) {
    end = end.plus({ days: 1 });
  }
  return {
    open: now >= start.minus({ minutes: JOIN_OPENS_MINUTES_BEFORE }) && now <= end,
    ended: now > end,
    startsAt: start,
  };
};

/** Как часто пересчитывать окно входа. */
const TICK_MS = 30_000;

/**
 * Текущее время как СОСТОЯНИЕ, а не разовый `DateTime.now()` при рендере.
 *
 * Окно входа зависит от «сейчас», но у занятия время статичное — посчитанное
 * один раз при монтировании оно так и застыло бы: студент, открывший страницу
 * за два часа до занятия и просто ждущий, никогда не увидел бы, как кнопка
 * включится (и как она погаснет по окончании). Тик раз в 30 секунд достаточно
 * точен для границы, заданной в минутах.
 */
const useNow = (): DateTime => {
  const [now, setNow] = useState(() => DateTime.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(DateTime.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);
  return now;
};

export interface ILessonZoomJoinContainer {
  currentLesson?: LessonType | null;
  activeRole: UserRole;
  /**
   * Группа «без расписания»: окно входа продолжает работать как обычно, но не
   * называет ни дату начала, ни «занятие сегодня» — иначе расписание утекло бы
   * именно там, где мы его прячем.
   */
  hideSchedule?: boolean;
  className?: string;
}

/**
 * Spec 0013: wires the join button to `lmsJoinLesson`. The new tab is opened
 * SYNCHRONOUSLY in the click handler (popup-blocker safe); the join URL only
 * arrives from the mutation because the JWT lives in Apollo headers — a plain
 * <a href> can't be authorized.
 */
export const LessonZoomJoinContainer = memo(
  ({ currentLesson, activeRole, hideSchedule = false, className }: ILessonZoomJoinContainer) => {
    const { t } = useTranslation();
    const { groupId, lessonId } = useParams();
    const { addToast } = useToaster();

    const [joinLesson, { loading: joinLoading }] = useJoinLesson();
    const { setTodayLink, clearTodayLink, setLoading, clearLoading } = useTodayLessonLink(
      Number(groupId),
    );

    const isTeacher = activeRole === UserRole.TEACHER;
    const hasJoinLink = Boolean(currentLesson?.hasJoinLink);

    const now = useNow();
    const {
      open: joinWindowOpen,
      ended,
      startsAt,
    } = useMemo(
      () =>
        buildJoinWindow(now, currentLesson?.date, currentLesson?.startTime, currentLesson?.endTime),
      [now, currentLesson?.date, currentLesson?.startTime, currentLesson?.endTime],
    );

    // Only the STUDENT is gated: a teacher legitimately opens the room early to
    // set up, and owns the temporary-link panel right below.
    const joinDisabled = !isTeacher && !joinWindowOpen;

    const joinHint = useMemo(() => {
      if (!joinDisabled) {
        return '';
      }
      if (ended) {
        return `${t('lesson.join.lessonEnded')}`;
      }
      // Без расписания подсказка обязана объяснить серую кнопку, но не может
      // назвать дату — иначе расписание утечёт через неё.
      if (hideSchedule) {
        return `${t('lesson.join.notAvailableYet')}`;
      }
      return startsAt
        ? `${t('lesson.join.startsAt', { when: startsAt.toFormat('dd.MM, HH:mm') })}`
        : '';
    }, [joinDisabled, ended, startsAt, hideSchedule, t]);

    const showJoinError = useCallback(() => {
      addToast('join_lesson', {
        type: ToastTypes.danger,
        text: `${t('lesson.join.joinError')}`,
      });
    }, [addToast, t]);

    const showPopupBlocked = useCallback(() => {
      addToast('join_lesson', {
        type: ToastTypes.warning,
        text: `${t('lesson.join.popupBlocked')}`,
      });
    }, [addToast, t]);

    const onJoin = useCallback(() => {
      // open the tab synchronously within the user gesture, then redirect it
      const joinWindow = window.open('', '_blank');
      if (joinWindow) {
        // Restores the `noopener` guarantee we give up by needing a handle on
        // the tab (same as the diplomas download): about:blank is still
        // same-origin here, and the disowning survives the navigation.
        joinWindow.opener = null;
      }

      joinLesson(Number(lessonId))
        .then(({ success, redirectUrl }) => {
          if (success && redirectUrl) {
            if (joinWindow) {
              joinWindow.location.href = redirectUrl;
            } else {
              // Blocked even though the open was synchronous (e.g. Safari's
              // "block all pop-ups") — surface it instead of letting the click
              // look like a no-op.
              showPopupBlocked();
            }
            return;
          }

          joinWindow?.close();
          showJoinError();
        })
        .catch(() => {
          joinWindow?.close();
          showJoinError();
        });
    }, [joinLesson, lessonId, showJoinError, showPopupBlocked]);

    const onSetLink = useCallback(
      async (joinUrl: string) => {
        const success = await setTodayLink(joinUrl);
        if (!success) {
          showJoinError();
        }
        return success;
      },
      [setTodayLink, showJoinError],
    );

    const onClearLink = useCallback(async () => {
      const success = await clearTodayLink();
      if (!success) {
        showJoinError();
      }
    }, [clearTodayLink, showJoinError]);

    if (!currentLesson || (!hasJoinLink && !isTeacher)) {
      return null;
    }

    return (
      <LessonZoomJoin
        className={className}
        showJoinButton={hasJoinLink}
        isToday={!hideSchedule && Boolean(currentLesson.date && isNow(currentLesson.date))}
        joinDisabled={joinDisabled}
        joinHint={joinHint}
        joinLoading={joinLoading}
        onJoin={onJoin}
        isTeacher={isTeacher}
        linkOverrideActive={Boolean(currentLesson.linkOverrideActive)}
        setLoading={setLoading}
        clearLoading={clearLoading}
        onSetLink={onSetLink}
        onClearLink={onClearLink}
      />
    );
  },
);
